/**
 * Configuração da Central: a cota gratuita do Gemini no dia. O total usado,
 * a hora em que volta (meia-noite do Pacífico, contada pelo servidor), a
 * pausa que o Google pediu e cada fatia do orçamento. Lista, não mural.
 */
import React from "react";
import { FiAlertCircle, FiPauseCircle } from "react-icons/fi";
import styled from "styled-components";

import { BP_MOBILE } from "../../styles/canonical/breakpoints";
import { nomeDoProvedor, resumoDoGeminiHoje } from "./gemini";
import Medidor from "./Medidor";
import { Item, Lista, Secao, SecaoTitulo, Vazio } from "./styles";

const CELULAR = `(max-width: ${BP_MOBILE}px)`;

const Total = styled.div`
  display: grid;
  gap: 8px;
  padding: 12px 14px 14px;

  > p {
    margin: 0;
    font-size: 14px;
    color: var(--ink-2);
    font-variant-numeric: tabular-nums;
  }

  > p b {
    font-size: 17px;
    font-weight: 700;
    color: var(--ink);
  }
`;

/* `div`, não `p`: o `> p` do Total pintaria o estado de cinza. */
const Estado = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ $tom }) => `var(--${$tom}-text)`};

  svg {
    flex: 0 0 auto;
  }
`;

const Fatia = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 112px minmax(120px, 200px);
  gap: 4px 16px;
  align-items: center;
  padding: 10px 14px;

  > div {
    display: grid;
    gap: 1px;
    min-width: 0;
  }

  b {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ink);
  }

  small {
    font-size: 12px;
    color: var(--ink-3);
  }

  @media ${CELULAR} {
    grid-template-columns: minmax(0, 1fr) auto;

    > [role="meter"] {
      grid-column: 1 / -1;
    }
  }
`;

const Conta = styled.span`
  font-size: 13px;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${({ $tom }) => ($tom === "danger" ? "var(--danger-text)" : "var(--ink-2)")};
  font-weight: ${({ $tom }) => ($tom === "danger" ? 600 : 400)};
`;

function Fatias({ fatias }) {
  return fatias.map((f) => (
    <Item key={f.chave}>
      <Fatia>
        <div>
          <b>{f.rotulo}</b>
          <small>{f.ajuda}</small>
        </div>
        <Conta $tom={f.tom}>
          {f.usados} de {f.limite}
          {f.esgotada ? " · esgotada" : ""}
        </Conta>
        <Medidor
          valor={f.limite ? f.usados / f.limite : 1}
          tom={f.tom}
          rotulo={`${f.rotulo}: pedidos usados hoje`}
          texto={`${f.usados} de ${f.limite}`}
        />
      </Fatia>
    </Item>
  ));
}

/** `gemini`: o `gemini_hoje` do `uso-ia`; `carregando` enquanto a leitura não voltou. */
export default function PainelGeminiHoje({ gemini, carregando }) {
  const resumo = resumoDoGeminiHoje(gemini);
  return (
    <Secao aria-labelledby="gemini-hoje">
      <SecaoTitulo>
        <h2 id="gemini-hoje">{nomeDoProvedor(gemini)} hoje</h2>
        <span>{resumo?.voltaAs ? `cota da empresa, volta às ${resumo.voltaAs}` : "cota da empresa"}</span>
      </SecaoTitulo>
      <Lista>
        {!resumo ? (
          <Vazio>{carregando ? "Lendo o uso de hoje…" : "O servidor ainda não informa a cota de hoje."}</Vazio>
        ) : (
          <>
            <Item>
              <Total>
                <p>
                  <b>{resumo.usados}</b> de {resumo.limite} pedidos usados hoje
                </p>
                <Medidor
                  valor={resumo.limite ? resumo.usados / resumo.limite : 1}
                  tom={resumo.tom}
                  rotulo="Pedidos ao Gemini usados hoje"
                  texto={`${resumo.usados} de ${resumo.limite}`}
                />
                {resumo.tom === "danger" ? (
                  <Estado $tom="danger">
                    <FiAlertCircle size={14} aria-hidden="true" />
                    {resumo.voltaAs ? `Cota do dia esgotada: o Gemini volta às ${resumo.voltaAs}.` : "Cota do dia esgotada."}
                  </Estado>
                ) : null}
                {resumo.pausadoAte ? (
                  <Estado $tom="warning">
                    <FiPauseCircle size={14} aria-hidden="true" />
                    {`Pausado até ${resumo.pausadoAte}: o Google pediu para esperar.`}
                  </Estado>
                ) : null}
              </Total>
            </Item>
            <Fatias fatias={resumo.fatias} />
          </>
        )}
      </Lista>
    </Secao>
  );
}
